import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import createBorderCss from '@oberoninternal/travelbase-ds/utils/createBorderCss';
import { Box } from '@rebass/grid';
import addMonths from 'date-fns/addMonths';
import React, { FC, HTMLAttributes, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import isDate from '../../../utils/isDate';
import { DropdownDescription } from '../Dropdown';
import StyledCaret from '../StyledCaret';
import { useDatepickerContext } from './Context';
import { getAssignmentColor } from '@oberoninternal/travelbase-ds/constants/theme';

interface Props {
    rounded?: boolean;
    withVerticalPadding?: boolean;
    startLabel?: ReactNode;
    endLabel?: ReactNode;
}

const SearchbarDatepickerInputs = ({ rounded, withVerticalPadding, startLabel, endLabel }: Props) => {
    const {
        datepicker: [{ period, open, inView }, { set }],
    } = useDatepickerContext();
    const { formatDate } = useIntl();
    const start = isDate(period) ? period : period?.start;
    const onClickInput = (focus: 'start' | 'end') => {
        set({
            inView: start ? [new Date(start), addMonths(start, 1)] : inView,
            open: true,
            focus,
        });
    };
    const onClickStart = () => onClickInput('start');
    const onClickEnd = () => onClickInput(!isDate(period) && period?.start ? 'end' : 'start');
    return (
        <StyledButton focus={open} rounded={rounded}>
            <Part
                withVerticalPadding={withVerticalPadding}
                onClick={onClickStart}
                label={startLabel ?? <FormattedMessage defaultMessage="Aankomst" />}
            >
                {!isDate(period) && period?.start ? (
                    <span>
                        {formatDate(period.start, {
                            format: 'DATERANGEPICKER',
                        })}
                    </span>
                ) : (
                    <FormattedMessage defaultMessage="Kies datum" />
                )}
            </Part>
            <Part
                withVerticalPadding={withVerticalPadding}
                onClick={onClickEnd}
                label={endLabel ?? <FormattedMessage defaultMessage="Vertrek" />}
            >
                {!isDate(period) && period?.end ? (
                    <span>
                        {formatDate(period.end, {
                            format: 'DATERANGEPICKER',
                        })}
                    </span>
                ) : (
                    <FormattedMessage defaultMessage="Kies datum" />
                )}
            </Part>
        </StyledButton>
    );
};

export default SearchbarDatepickerInputs;

interface PartProps extends HTMLAttributes<HTMLButtonElement> {
    label: ReactNode;
    withVerticalPadding?: boolean;
}

const Part: FC<React.PropsWithChildren<PartProps>> = ({ label, children, ...props }) => (
    <StyledPart type="button" {...props}>
        <Box>
            <StyledBody>{label}</StyledBody>
            <StyledDropdownDescription variant="small" isPlaceholder={false}>
                {children}
            </StyledDropdownDescription>
        </Box>
        <StyledCaret />
    </StyledPart>
);

const StyledPart = styled.button<{ withVerticalPadding?: boolean }>`
    display: flex;
    flex: 1;
    height: 100%;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme, withVerticalPadding }) =>
        `${withVerticalPadding ? theme.spacing['30_Small'] : 0} ${theme.spacing['40_Standard']}`};
    :first-of-type {
        position: relative;
        ::after {
            content: '';
            background-color: ${({ theme }) => theme.colors.neutral['20']};
            position: absolute;
            width: 1px;
            height: 70%;
            top: calc(50% - 70% / 2);
            right: -0.2rem;
            display: block;
        }
    }
    > svg {
        stroke: ${({ theme }) => theme.colors.neutral['40']};
    }
    :hover {
        > svg {
            stroke: ${({ theme }) => theme.colors.neutral['90']};
        }
    }
`;

const StyledButton = styled.div<{ focus: boolean; rounded?: boolean }>`
    height: 100%;
    background: ${({ theme }) => theme.colors.neutral['0']};

    width: 100%;
    padding: 0;
    display: flex;
    border-radius: ${({ theme, rounded }) => rounded && theme.radius.textInput};
    ${createBorderCss('2px')};
    --border-color: ${({ theme }) => theme.colors.neutral['20']};

    :hover {
        --border-color: ${({ theme }) => theme.colors.neutral['40']};
    }

    ${({ focus, theme }) => focus && `--border-color: ${getAssignmentColor(theme, theme.colorAssignments.input)}`};

    :hover,
    :focus {
        box-shadow: 0 0 0 1.5px var(--border-color);
    }
    transition: box-shadow 0.15s ease-in-out;
`;

const StyledDropdownDescription = styled(DropdownDescription)<{ isPlaceholder: boolean }>`
    ${({ isPlaceholder, theme }) => isPlaceholder && `color: ${theme.colors.neutral['40']}`};
`;

const StyledBody = styled(Body).attrs({ variant: 'tiny' })`
    color: ${({ theme }) => theme.colors.neutral['60']};
    font-weight: 300 !important;
    display: flex;
    line-height: 1.15;
    white-space: nowrap;
`;
